import { warn } from '@ember/debug';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import {
  addDays,
  addYears,
  endOfDay,
  endOfMonth,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  isWeekend,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import type StateService from 'garaje/services/state';
import type ActiveDayConfig from 'garaje/types/date-time/active-day-config';
import type DateLimits from 'garaje/types/date-time/date-limits';
import type DayConfig from 'garaje/types/date-time/day-config';
import {
  DATE_FNS_YYYY_MM_DD,
  DEFAULT_TIMEZONE,
  ensurePlainJsDate,
  isInDateRange,
  isMoment,
  modifyDateInTimeZone,
} from 'garaje/utils/date-fns-tz-utilities';
import { type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';
import { MINUTES_IN_A_DAY } from 'garaje/utils/enums';
import { getReadableScheduleEligibilityOffset } from 'garaje/utils/getReadableScheduleEligibilityOffset';

interface DayCellComponentSignature {
  Args: {
    activeDays?: ActiveDayConfig[];
    currentMonth?: CalendarDate;
    date: CalendarDate;
    dateLimits?: DateLimits;
    daysToCustomize?: DayConfig[];
    hasEvents?: boolean;
    hideLeadingDayCells?: boolean;
    hideTrailingDayCells?: boolean;
    respectSchedulingLimits?: boolean;
    selectedDates?: CalendarDate[];
    selectedEndDate?: CalendarDate;
    selectedStartDate?: CalendarDate;
    timezone?: string;
    selectDate?: (date: Date) => void;
  };
  Element: HTMLTableCellElement;
}

export default class DayCellComponent extends Component<DayCellComponentSignature> {
  // Use @args instead of consuming state service in otherwise portable component
  @service declare state: StateService;

  constructor(...args: ConstructorParameters<typeof Component<DayCellComponentSignature>>) {
    super(...args);

    const { currentMonth, date } = this.args;

    if (currentMonth) {
      warn('DayCellComponent: @currentMonth uses an instance of Moment.js', !isMoment(currentMonth), {
        id: 'envoy.day-cell.moment-instance',
      });
    }

    warn('DayCellComponent: @date uses an instance of Moment.js', !isMoment(date), {
      id: 'envoy.day-cell.moment-instance',
    });
  }

  get timezone(): string {
    return this.args.timezone || DEFAULT_TIMEZONE;
  }

  get currentMonth(): Date {
    return ensurePlainJsDate(this.args.currentMonth || new Date(), this.timezone);
  }

  get date(): Date {
    return ensurePlainJsDate(this.args.date, this.timezone);
  }

  get dateFormatted(): string {
    return formatInTimeZone(this.date, this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get dayNumber(): string {
    return formatInTimeZone(this.date, this.timezone, 'd');
  }

  get dayName(): string {
    return formatInTimeZone(this.date, this.timezone, 'EEEE');
  }

  get beginningOfCurrentMonth(): Date {
    const { timezone, currentMonth } = this;

    return modifyDateInTimeZone(currentMonth, timezone, startOfMonth);
  }

  get endOfCurrentMonth(): Date {
    const { timezone, beginningOfCurrentMonth } = this;

    return modifyDateInTimeZone(beginningOfCurrentMonth, timezone, endOfMonth);
  }

  get isToday(): boolean {
    const today = formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);

    return this.dateFormatted === today;
  }

  get isDateInCurrentMonth(): boolean {
    const currentMonthFormatted = formatInTimeZone(this.currentMonth, this.timezone, 'MM');
    const dateMonthFormatted = formatInTimeZone(this.date, this.timezone, 'MM');

    return currentMonthFormatted === dateMonthFormatted;
  }

  get selectedStartDate(): Date | null {
    const { selectedStartDate } = this.args;

    if (!selectedStartDate) return null;

    return ensurePlainJsDate(selectedStartDate, this.timezone);
  }

  get selectedEndDate(): Date | null {
    const { selectedEndDate } = this.args;

    if (!selectedEndDate) return null;

    return ensurePlainJsDate(selectedEndDate, this.timezone);
  }

  get isSelectedStartDate(): boolean {
    const { selectedStartDate, dateFormatted, timezone } = this;
    const selectedStartDateFormatted = selectedStartDate
      ? formatInTimeZone(selectedStartDate, timezone, DATE_FNS_YYYY_MM_DD)
      : '';

    return dateFormatted === selectedStartDateFormatted;
  }

  get isSelectedEndDate(): boolean {
    const { selectedEndDate, dateFormatted, timezone } = this;
    const selectedEndDateFormatted = selectedEndDate
      ? formatInTimeZone(selectedEndDate, timezone, DATE_FNS_YYYY_MM_DD)
      : '';

    return dateFormatted === selectedEndDateFormatted;
  }

  get isDateInSelection(): boolean {
    const { date, selectedStartDate, selectedEndDate, isSelectedStartDate, isSelectedEndDate } = this;

    if (!(selectedStartDate && selectedEndDate)) return false;

    const isInRange = isInDateRange(date, selectedStartDate, selectedEndDate);

    return isInRange || isSelectedStartDate || isSelectedEndDate;
  }

  get shouldHighlightCell(): boolean {
    const {
      date,
      beginningOfCurrentMonth,
      endOfCurrentMonth,
      args: { hideTrailingDayCells, hideLeadingDayCells },
    } = this;

    if (isBefore(date, beginningOfCurrentMonth) && hideLeadingDayCells) return false;
    if (isAfter(date, endOfCurrentMonth) && hideTrailingDayCells) return false;

    return true;
  }

  get classes(): string {
    const {
      beginningOfCurrentMonth,
      date,
      dayIsActive,
      dayIsOutOfSchedule,
      endOfCurrentMonth,
      isDateInCurrentMonth,
      isSelectedEndDate,
      isSelectedStartDate,
      isToday,
      isWithinLimits,
      selectedEndDate,
      selectedStartDate,
      shouldHighlightCell,
      args: { hideTrailingDayCells, hideLeadingDayCells },
    } = this;
    const isLeadingCell = isBefore(date, beginningOfCurrentMonth);
    const isTrailingCell = isAfter(date, endOfCurrentMonth);
    const classes = [];

    if (isToday && isDateInCurrentMonth) classes.push('today');
    if (isWeekend(date)) classes.push('weekend');

    if (isSelectedStartDate && shouldHighlightCell) {
      classes.push('range-boundary');
      classes.push('startAt');
    }

    if (isSelectedEndDate && shouldHighlightCell) {
      classes.push('range-boundary');
      classes.push('endAt');
    }

    if (
      selectedStartDate &&
      !isSelectedStartDate &&
      selectedEndDate &&
      !isSelectedEndDate &&
      shouldHighlightCell &&
      isInDateRange(date, selectedStartDate, selectedEndDate)
    ) {
      classes.push('range');
    }

    if (isLeadingCell) {
      classes.push(hideLeadingDayCells ? 'pointer-events-none' : 'past');
    }

    if (isTrailingCell) {
      classes.push(hideTrailingDayCells ? 'pointer-events-none' : 'future');
    }

    if (!isWithinLimits || !dayIsActive || dayIsOutOfSchedule) {
      const inactiveClasses = ['line-through', 'leading-6', 'out-of-limits', 'disabled'];

      if (!dayIsOutOfSchedule) inactiveClasses.push('pointer-events-none');

      classes.push(...inactiveClasses);
    }

    return [...new Set(classes)].join(' ');
  }

  get dayClasses(): string {
    const {
      beginningOfCurrentMonth,
      date,
      dateFormatted,
      dayIsOutOfSchedule,
      dayToCustomize,
      endOfCurrentMonth,
      isDateInSelection,
      isWithinLimits,
      selectedEndDate,
      selectedStartDate,
      timezone,
      args: { hideTrailingDayCells, hideLeadingDayCells, selectedDates },
    } = this;

    const classes = [];

    if (isWithinLimits) {
      if (dayToCustomize) {
        const customClasses = (dayToCustomize.class || '').split(' ');

        classes.push(...customClasses);
      } else if (!isDateInSelection && !dayIsOutOfSchedule) {
        classes.push('hoverable');
      }
    }

    if (selectedDates) {
      const dateIsSelected = selectedDates.find(
        (selected) =>
          dateFormatted === formatInTimeZone(ensurePlainJsDate(selected, timezone), timezone, DATE_FNS_YYYY_MM_DD),
      );

      if (!selectedStartDate && !selectedEndDate && dateIsSelected) {
        classes.push('bg-carbon-10', 'text-carbon-70', 'rounded-full', 'py-1', 'selected-date');
      }
    } else {
      if (isBefore(date, beginningOfCurrentMonth)) {
        classes.push(hideLeadingDayCells ? 'hidden' : 'text-carbon-10');
      }

      if (isAfter(date, endOfCurrentMonth)) {
        classes.push(hideTrailingDayCells ? 'hidden' : 'text-carbon-10');
      }
    }

    return [...new Set(classes)].join(' ');
  }

  get dayIsOutOfSchedule(): boolean {
    const {
      date,
      timezone,
      args: { respectSchedulingLimits },
    } = this;

    if (!respectSchedulingLimits) return false;

    const locationDate = modifyDateInTimeZone(new Date(), timezone, startOfDay);
    const endOfEligibilityDate = this.getEndOfEligibilityDate(locationDate);

    if (!endOfEligibilityDate) return false;

    return getUnixTime(date) > endOfEligibilityDate;
  }

  get tooltip(): string {
    if (this.dayToCustomize?.tooltip) {
      return this.dayToCustomize.tooltip;
    }

    if (this.dayIsOutOfSchedule) {
      const offsetText = getReadableScheduleEligibilityOffset(this.state.currentLocation.scheduleEligibilityOffset);
      return `Your company only allows you to schedule up to ${offsetText} in advance`;
    }

    return '';
  }

  get dayIsActive(): boolean {
    const {
      date,
      timezone,
      args: { activeDays },
    } = this;

    if (!activeDays) return true;

    const dayOfWeek = formatInTimeZone(date, timezone, 'EEEE');

    return activeDays.some((day) => day.dayOfWeek === dayOfWeek);
  }

  get dayToCustomize(): DayConfig | null {
    const {
      dateFormatted,
      timezone,
      args: { daysToCustomize },
    } = this;

    if (!daysToCustomize) return null;

    const dayToCustomize = daysToCustomize.find((day) => {
      const customDateFormatted = formatInTimeZone(fromUnixTime(day.date), timezone, DATE_FNS_YYYY_MM_DD);

      return customDateFormatted === dateFormatted;
    });

    return dayToCustomize || null;
  }

  get isWithinLimits(): boolean {
    const {
      date,
      timezone,
      args: { dateLimits },
    } = this;

    if (!dateLimits) return true;

    const { startDate, endDate } = dateLimits;
    const minDate = startDate ? modifyDateInTimeZone(fromUnixTime(startDate), timezone, startOfDay) : null;
    const maxDate = endDate ? modifyDateInTimeZone(fromUnixTime(endDate), timezone, endOfDay) : null;

    if (minDate && isBefore(date, minDate)) return false;
    if (maxDate && isAfter(date, maxDate)) return false;

    return true;
  }

  getEndOfEligibilityDate(locationDate: Date): number {
    const endOfScheduleEligibility = this.state.currentLocation.scheduleEligibilityOffset;

    if (!endOfScheduleEligibility) return getUnixTime(addYears(locationDate, 2));

    const daysToAdd = endOfScheduleEligibility / -MINUTES_IN_A_DAY - 1;
    const eligibilityBoundary = modifyDateInTimeZone(addDays(locationDate, daysToAdd), this.timezone, endOfDay);

    return getUnixTime(eligibilityBoundary);
  }

  @action
  selectDate(): void {
    const {
      date,
      args: { selectDate },
    } = this;

    if (!this.dayToCustomize?.disabled && !this.dayIsOutOfSchedule) {
      selectDate?.(date);
    }
  }

  <template>
    <td
      {{on "click" this.selectDate}}
      id="date{{this.dateFormatted}}"
      data-test-day-cell={{this.dateFormatted}}
      data-test-day-of-week={{this.dayName}}
      class="{{this.classes}} w-8 m-auto"
      ...attributes
    >
      <div
        class="{{if @hasEvents 'events'}}
          {{this.dayClasses}}
          w-6 h-6 flex items-center justify-center pointer-events-none"
        data-test-day-cell-content={{this.dateFormatted}}
      >
        {{this.dayNumber}}
      </div>
    </td>
    {{#if this.tooltip}}
      <EmberTooltip
        @targetId="date{{this.dateFormatted}}"
        @side="top"
        @tooltipClass="rounded-lg !p-3"
        data-test-calendar-tooltip
      >
        {{! template-lint-disable no-triple-curlies }}
        {{{this.tooltip}}}
      </EmberTooltip>
    {{/if}}
  </template>
}
